// src/lib/pwa/install-prompt.ts
//
// "Should we offer to install the app right now?" — the pure half of the
// install prompt, kept free of React/DOM so it can be unit-tested.
//
// Why this exists: the app has been installable for a while (manifest +
// service worker + standalone display), but nothing ever ASKED. Chrome fires
// `beforeinstallprompt` and, if nobody calls preventDefault + prompt(), the
// only affordance is a menu item most students never open. iOS is worse — it
// has no install event at all; Add to Home Screen is buried in the Share
// sheet, so it has to be taught with instructions.
//
// The hard part is not showing the banner, it's not being obnoxious. The rules
// below are deliberately conservative: never when already installed, never
// mid-question (a banner sliding in during an SAT module is the same class of
// bug as the deploy-reload we fixed in session-activity), a long cooldown after
// a dismissal, and a hard stop after a couple of refusals.

/**
 * How the current browser can install.
 *   - 'installable' — Chrome/Edge/Android fired `beforeinstallprompt`; we can
 *     drive a real install dialog.
 *   - 'ios'         — WebKit on iOS/iPadOS. No API; show Share-sheet steps.
 *   - 'unsupported' — desktop Safari, Firefox, embedded webviews, or anything
 *     that gave us no signal. Say nothing rather than give wrong directions.
 */
export type InstallPlatform = 'installable' | 'ios' | 'unsupported'

/** Persisted nag-control state (localStorage). All fields optional/legacy-safe. */
export type InstallPromptState = {
  /** Epoch ms of the most recent dismissal. */
  dismissedAt?: number
  /** How many times the user has dismissed. */
  dismissCount?: number
  /** Epoch ms when we observed a successful install (`appinstalled`). */
  installedAt?: number
}

export const INSTALL_PROMPT_STORAGE_KEY = 'ut:install-prompt'

/** Wait this long after a dismissal before asking again. */
export const INSTALL_DISMISS_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

/** After this many dismissals, never ask again. Two "no"s is an answer. */
export const INSTALL_MAX_DISMISSALS = 2

/**
 * Delay before the banner appears, so it never competes with first paint or
 * with a student who opened the app to do one specific thing.
 */
export const INSTALL_PROMPT_DELAY_MS = 4000

/** iOS/iPadOS detection, including iPadOS 13+ which reports itself as a Mac. */
export function isIosDevice(opts: {
  userAgent: string
  platform?: string
  maxTouchPoints?: number
}): boolean {
  if (/iPad|iPhone|iPod/.test(opts.userAgent)) return true
  // iPadOS 13+ ships a desktop UA string; the only reliable tell is a
  // "Macintosh" that also reports touch points.
  return opts.platform === 'MacIntel' && (opts.maxTouchPoints ?? 0) > 1
}

/**
 * Embedded webviews (Instagram, Facebook, etc.) render pages on iOS but have
 * no Share → Add to Home Screen. Telling a student to tap a button that isn't
 * there is worse than staying quiet.
 */
export function isEmbeddedWebview(userAgent: string): boolean {
  return /FBAN|FBAV|Instagram|Line\/|Twitter|LinkedInApp|Snapchat|Pinterest|MicroMessenger/i.test(
    userAgent,
  )
}

/**
 * Classify the browser. `hasDeferredPrompt` means we captured a real
 * `beforeinstallprompt` event, which trumps everything — it is a direct
 * statement from the browser that installation is available.
 */
export function detectInstallPlatform(opts: {
  userAgent: string
  platform?: string
  maxTouchPoints?: number
  hasDeferredPrompt: boolean
}): InstallPlatform {
  if (opts.hasDeferredPrompt) return 'installable'
  if (isEmbeddedWebview(opts.userAgent)) return 'unsupported'
  if (isIosDevice(opts)) return 'ios'
  return 'unsupported'
}

/**
 * The decision. Pure so the nag rules are pinned by tests rather than
 * discovered in production.
 *
 * A `dismissedAt` in the FUTURE (clock skew, or a hand-edited value) is
 * ignored rather than honoured — otherwise one bad timestamp would suppress
 * the prompt permanently, which is a worse failure than showing it once more.
 */
export function shouldShowInstallPrompt(opts: {
  platform: InstallPlatform
  /** Already running as an installed app — nothing to offer. */
  isStandalone: boolean
  /** Mid-question / mid-module. Never interrupt. */
  sessionActive: boolean
  state: InstallPromptState
  nowMs: number
}): boolean {
  if (opts.platform === 'unsupported') return false
  if (opts.isStandalone) return false
  if (opts.sessionActive) return false
  if (typeof opts.state.installedAt === 'number') return false
  if ((opts.state.dismissCount ?? 0) >= INSTALL_MAX_DISMISSALS) return false

  const dismissedAt = opts.state.dismissedAt
  if (
    typeof dismissedAt === 'number' &&
    dismissedAt <= opts.nowMs &&
    opts.nowMs - dismissedAt < INSTALL_DISMISS_COOLDOWN_MS
  ) {
    return false
  }
  return true
}

/** Parse persisted state, tolerating absent/corrupt/foreign values. */
export function parseInstallPromptState(raw: string | null | undefined): InstallPromptState {
  if (!raw) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const rec = parsed as Record<string, unknown>
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)
    return {
      dismissedAt: num(rec.dismissedAt),
      dismissCount: num(rec.dismissCount),
      installedAt: num(rec.installedAt),
    }
  } catch {
    return {}
  }
}

/** State after the user dismisses the banner. */
export function nextStateAfterDismiss(
  state: InstallPromptState,
  nowMs: number,
): InstallPromptState {
  return { ...state, dismissedAt: nowMs, dismissCount: (state.dismissCount ?? 0) + 1 }
}

/** State after a confirmed install. */
export function nextStateAfterInstall(
  state: InstallPromptState,
  nowMs: number,
): InstallPromptState {
  return { ...state, installedAt: nowMs }
}
